import { Trans } from '@lingui/react/macro';
import { startOfDay } from 'date-fns/startOfDay';

import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconClock from '@/icons/Clock.svg';
import IconDone from '@/icons/Done.svg';
import IconTruck from '@/icons/Truck.svg';
import { getArrivalDate, getDestination, ShipmentWithRelations } from '@/models/shipment';
import { createBEMClasses } from '@/utils/classname';
import { formatDate } from '@/utils/date';
import useViewPort from '@/utils/useViewport';

import { useDisplayedShipmentCountry } from '../hooks/useDisplayedShipmentCountry';

import './ShipmentDestination.css';

const { block, element } = createBEMClasses('shipment-destination');

export const ShipmentDestination = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const { destinationCountry } = useDisplayedShipmentCountry({ shipment });
  const { isMobile } = useViewPort();

  const destination = getDestination(shipment);

  if (!destination) {
    return '-';
  }

  return (
    <Stack gap={isMobile ? undefined : '0.125rem'} className={block()}>
      <Stack
        row={isMobile}
        alignItems={isMobile ? 'center' : 'flex-start'}
        gap={isMobile ? '0.25rem' : '0'}
      >
        <InlineText weight="medium">{destination.name}</InlineText>
        {isMobile && (
          <InlineText size="200" color="secondary">
            •
          </InlineText>
        )}
        <InlineText size="200" color="secondary">
          {[
            shipment.destinationAddress?.city,
            shipment.destinationAddress?.state,
            destinationCountry?.name,
          ]
            .filter((el) => !!el)
            .join(', ')}
        </InlineText>
      </Stack>
      <ShipmentArrivalDate shipment={shipment} />
    </Stack>
  );
};

const ShipmentArrivalDate = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const arrivalDate = getArrivalDate(shipment);
  const updatedEtaDate = shipment.updatedEta ? new Date(shipment.updatedEta) : null;
  const originalEtaDate = shipment.originalEta ? new Date(shipment.originalEta) : null;

  if (arrivalDate || (shipment.carrier === 'private-carrier' && originalEtaDate)) {
    return (
      <span className={element('arrival-date', { arrived: true })}>
        <IconDone /> {formatDate(arrivalDate ?? originalEtaDate!, { dateStyle: 'short' })}
      </span>
    );
  }

  const eta = updatedEtaDate ?? originalEtaDate;

  if (eta) {
    const isLate = startOfDay(eta) < startOfDay(new Date());

    if (isLate) {
      return (
        <span className={element('arrival-date', { late: true })}>
          <IconClock /> {formatDate(eta, { dateStyle: 'short' })}{' '}
          <Trans id="shipments.estimated.short">(est.)</Trans>
        </span>
      );
    }

    return (
      <span className={element('arrival-date', { transit: true })}>
        <IconTruck /> {formatDate(eta, { dateStyle: 'short' })}{' '}
        <Trans id="shipments.estimated.short">(est.)</Trans>
      </span>
    );
  }

  return null;
};
